import { observer } from "mobx-react-lite";
import { Box, Stack, Typography } from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";

// The "Unified Output" section: what every service exports. The per-service texts live in the
// services panel beside the drop zone, so the header doesn't repeat them.
const OUTPUT_POINTS = [
  "ocrService.info.output.point1",
  "ocrService.info.output.point2",
  "ocrService.info.output.point3",
  "ocrService.info.output.point4",
  "ocrService.info.output.point5",
];

interface Props {
  // Include the Unified Output section. Off once a file is open: by then the export, review and
  // backup actions are on screen themselves, so the header only needs the definition.
  showOutput?: boolean;
}

/** The Inspection Insights header text: what the app is, plus the shared exports. */
export default observer(function InspectionInsightsDescription({ showOutput = true }: Props) {
  return (
    // One box for the definition and the outputs, so both share its width. The cap keeps the
    // definition to about three lines instead of one long line across the header.
    <Box sx={{ maxWidth: { lg: 720 } }}>
      <Typography variant="body1" color="GrayText" sx={{ mb: showOutput ? 2 : 0, lineHeight: 1.7, textAlign: "justify" }}>
        {translationService.translate("ocrService.info.definition")}
      </Typography>
      {showOutput && (
        <Box>
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>
            {translationService.translate("ocrService.info.output.title")}
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {OUTPUT_POINTS.map((pointKey) => (
              <Stack key={pointKey} direction="row" spacing={0.75} alignItems="flex-start">
                <CheckCircleOutline color="primary" sx={{ fontSize: 16, mt: "2px" }} />
                <Typography variant="caption" color="text.primary">
                  {translationService.translate(pointKey)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
});
